import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ServicePageHeader } from "@/components/ServiceHistoryButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Signal } from "lucide-react";

const OPERATORS = ["TIM", "Vodafone", "WindTre", "Iliad", "PosteMobile"];

export default function SimActivation() {
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ customer_id: "", iccid: "", phone_number: "", operator: "", plan_product_id: "", activation_type: "new" });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/products", { params: { category: "sim" } }).then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.operator) { toast.error("Seleziona cliente e operatore"); return; }
    setLoading(true);
    try {
      const payload = { ...form, plan_product_id: form.plan_product_id || null };
      await api.post("/sims/activate", payload);
      toast.success("SIM attivata con successo");
      setForm({ customer_id: "", iccid: "", phone_number: "", operator: "", plan_product_id: "", activation_type: "new" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore attivazione");
    } finally { setLoading(false); }
  };

  return (
    <div className="service-page-wide space-y-5 w-full max-w-none" data-testid="sim-activation-page">
      <ServicePageHeader service="sim">
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Servizi</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Attivazione SIM</h1>
      </ServicePageHeader>
      <Card className="p-5 glow-border">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Cliente</Label>
            <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="sim-customer-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — <PhoneDisplay value={c.phone} data-testid={`sim-customer-phone-${c.id}`} /></SelectItem>)}
              </SelectContent>
            </Select>
            {customers.length === 0 && <p className="text-xs text-amber-500 mt-1">Aggiungi prima un cliente nella sezione Clienti.</p>}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Operatore</Label>
              <Select value={form.operator} onValueChange={(v) => setForm({ ...form, operator: v })}>
                <SelectTrigger className="mt-1.5" data-testid="sim-operator-select"><SelectValue placeholder="Operatore" /></SelectTrigger>
                <SelectContent>{OPERATORS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo attivazione</Label>
              <Select value={form.activation_type} onValueChange={(v) => setForm({ ...form, activation_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="sim-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Nuovo numero</SelectItem>
                  <SelectItem value="portability">Portabilità (MNP)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="iccid">ICCID (numero SIM)</Label>
              <Input id="iccid" required className="mt-1.5" data-testid="sim-iccid" value={form.iccid} onChange={(e) => setForm({ ...form, iccid: e.target.value })} placeholder="8939..." />
            </div>
            <div>
              <Label htmlFor="phone">Numero di telefono</Label>
              <Input type="tel" id="phone" required className="mt-1.5" data-testid="sim-phone" value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="+39 3xx..." />
            </div>
          </div>
          <div>
            <Label>Piano tariffario (opzionale)</Label>
            <Select value={form.plan_product_id} onValueChange={(v) => setForm({ ...form, plan_product_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="sim-plan-select"><SelectValue placeholder="Seleziona piano" /></SelectTrigger>
              <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={loading} className="gap-2" data-testid="sim-activate-button">
            <Signal className="w-4 h-4" /> {loading ? "Attivazione…" : "Attiva SIM"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
